import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Save } from 'lucide-react';
import apiClient from '../lib/apiClient';
import { useLocale } from '../contexts/LocaleContext';
import { getAppLocale } from '../content/appLocale';
import GenerationShell from '../components/GenerationShell';
import AccountNavigation from '../components/AccountNavigation';
import LocaleSelector from '../components/LocaleSelector';

const Settings = () => {
  const { locale } = useLocale();
  const copy = getAppLocale(locale);
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileLoading(true);

    try {
      await apiClient.put('/api/auth/profile', { name, email });
      await refreshUser();
      setProfileSuccess(copy.settings.profileUpdated);
    } catch (error) {
      setProfileError(error.response?.data?.detail || copy.settings.profileUpdateFailed);
    } finally {
      setProfileLoading(false);
    }
  };

  return (
    <GenerationShell>
      <div className="studio-account-page">
        <div className="studio-page-heading"><div><h1>{copy.settings.title}</h1><p>Manage your profile and workspace preferences.</p></div></div>
        <AccountNavigation />
        <section data-testid="profile-section" className="studio-settings-section">
          <div><h2>{copy.settings.profile}</h2><p>{copy.settings.profileHelp}</p></div>
          <form onSubmit={handleUpdateProfile} className="studio-settings-form">
            {profileError && <div role="alert" data-testid="profile-error" className="studio-alert studio-alert-error">{profileError}</div>}
            {profileSuccess && <div role="status" data-testid="profile-success" className="studio-account-success">{profileSuccess}</div>}
            <div><label htmlFor="profile-name" className="studio-field-label">{copy.auth.fullName}</label>
              <input id="profile-name" autoComplete="name" data-testid="name-input" type="text" value={name} onChange={(e) => { setName(e.target.value); setProfileSuccess(''); }} className="studio-input" placeholder={copy.settings.namePlaceholder} required />
            </div>
            <div><label htmlFor="profile-email" className="studio-field-label">{copy.auth.emailAddress}</label>
              <input id="profile-email" autoComplete="email" data-testid="email-input" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setProfileSuccess(''); }} className="studio-input" placeholder={copy.settings.emailPlaceholder} required />
            </div>
            <div className="studio-settings-actions"><button data-testid="update-profile-button" type="submit" disabled={profileLoading} className="studio-button studio-button-primary"><Save size={16} aria-hidden /><span>{profileLoading ? copy.settings.saving : copy.settings.saveChanges}</span></button></div>
          </form>
        </section>
        <section className="studio-settings-section"><div><h2>{copy.common.localeLabel}</h2></div><LocaleSelector variant="menu" className="studio-account-locale" /></section>
        <footer className="studio-account-footer"><p>{copy.settings.legalLinksIntro}</p><nav aria-label={copy.profileMenu.legalHeading}>
          <Link to="/privacy">{copy.profileMenu.privacyPolicy}</Link><Link to="/terms">{copy.profileMenu.termsOfService}</Link><Link to="/refund-policy">{copy.profileMenu.refundPolicy}</Link><Link to="/help">{copy.profileMenu.helpSupport}</Link>
        </nav></footer>
      </div>
    </GenerationShell>
  );
};

export default Settings;
